import React, { useMemo } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { Tree } from 'react-arborist';
import { AutoSizer } from 'react-virtualized';

import hoverActions from 'js/redux/reducers/HoverProduct';
import { rightToLeft } from '../../../mylib/Utils';

// Helper functions for ProductTree component

const filterGroups = (filtered_products, product_groups) => {
  const prodmap = new Map(filtered_products.map((p) => [p.productid, p]));
  const groups = product_groups.map((group) => ({
    ...group,
    products: group.products
      .filter((p) => prodmap.has(p.productid))
      .map((p) => {
        const product = prodmap.get(p.productid);
        return { ...product, name: product.productzname };
      }),
  }));

  return groups;
};

const searchSubTree = (node, id) =>
  node.id === id || !!node.children.find((x) => searchSubTree(x, id));

const addProductsAndPrune = (node) => {
  let { children, products } = node;
  children = children.map(addProductsAndPrune).filter(Boolean);
  if (children.length + products.length === 0) {
    node.children = [];
    return null;
  }
  node.children = [...children, ...products];
  return node;
};

const nestGroups = (groups) => {
  const nodes = new Map(
    groups.map((g) => [
      g.prodgroupid,
      {
        name: g.prodgroupname,
        id: g.prodgroupid,
        parentid: g.parentgroupid,
        children: [],
        products: g.products,
      },
    ])
  );
  const root = { children: [], products: [] };
  nodes.set(null, root);

  // Add each node to parent's children
  for (const node of nodes.values()) {
    const { parentid } = node;
    // Search to prevent cycles in case of bad data
    if (!searchSubTree(node, parentid)) {
      nodes.get(parentid).children.push(node);
    }
  }

  // Append products to children and prune empty groups
  addProductsAndPrune(root);
  return root.children;
};

const getNodeId = (node) =>
  node.productid == null ? `group-${node.id}` : `product-${node.productid}`;

const ProductTreeNode = ({ node, isRtl, onHover, onSelect }) => {
  const item = node.data;

  return (
    <button
      type="button"
      className="config-tree-node"
      data-testid={'tree_item_' + item.name}
      onClick={(event) => {
        event.stopPropagation();
        if (node.isLeaf) {
          onSelect(item);
        } else {
          node.toggle();
        }
      }}
      onMouseEnter={() => onHover(item)}
      onMouseLeave={() => onHover(null)}
      style={{
        '--config-tree-indent': `${node.level * 19}px`,
      }}
    >
      <span aria-hidden="true" className="config-tree-node__toggle">
        {!node.isLeaf && (node.isOpen ? '▼' : isRtl ? '◀' : '▶')}
      </span>
      <span className="config-tree-node__label">{item.name}</span>
    </button>
  );
};

ProductTreeNode.propTypes = {
  node: PropTypes.shape({
    data: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }).isRequired,
    isLeaf: PropTypes.bool.isRequired,
    isOpen: PropTypes.bool.isRequired,
    level: PropTypes.number.isRequired,
    toggle: PropTypes.func.isRequired,
  }).isRequired,
  isRtl: PropTypes.bool.isRequired,
  onHover: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
};

// The component

const ProductTree = (props) => {
  const { filtered_products, product_groups } = props;
  const isRtl = rightToLeft();
  const data = useMemo(
    () => nestGroups(filterGroups(filtered_products, product_groups)),
    [filtered_products, product_groups]
  );

  const treeHover = (node) => {
    if (props.hover_product.product?.productid !== node?.productid) {
      props.setHoverProduct(node);
    }
  };

  const { t } = props;
  return (
    <div
      className="bordered br-8"
      dir={isRtl ? 'rtl' : 'ltr'}
      style={{
        ...props.scrollstyle,
        backgroundColor: '#112e40',
        paddingLeft: isRtl ? 0 : '8px',
        paddingRight: isRtl ? '8px' : 0,
        textAlign: isRtl ? 'right' : 'left',
      }}
    >
      {data.length === 0 ? (
        <div
          style={{
            backgroundColor: 'lightblue',
            borderTop: '1px solid #dce4e9',
            height: '40px',
            padding: '5px',
          }}
        >
          {t('lbl.noProducts', 'No products')}
        </div>
      ) : (
        <AutoSizer>
          {({ width, height }) => (
            <Tree
              aria-label={t('lbl.products', 'Products')}
              className="scroll no-focus"
              data={data}
              disableDrag
              disableDrop
              disableEdit
              disableMultiSelection
              disableSelect
              height={height}
              idAccessor={getNodeId}
              indent={19}
              openByDefault
              rowHeight={24}
              width={width}
            >
              {(nodeProps) => (
                <ProductTreeNode
                  {...nodeProps}
                  isRtl={isRtl}
                  onHover={treeHover}
                  onSelect={props.onSelect}
                />
              )}
            </Tree>
          )}
        </AutoSizer>
      )}
    </div>
  );
};

ProductTree.propTypes = {
  filtered_products: PropTypes.arrayOf(PropTypes.object).isRequired,
  scrollstyle: PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired,
  product_groups: PropTypes.arrayOf(PropTypes.object).isRequired,
  hover_product: PropTypes.object.isRequired,
  setHoverProduct: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    product_groups: state.cache.product_groups,
    hover_product: state.hover_product,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      setHoverProduct: hoverActions.setHoverProduct,
    },
    dispatch
  );
}

export default withTranslation('translations')(
  connect(mapStateToProps, mapDispatchToProps)(ProductTree)
);
